import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { api, photoUrl } from "@/lib/api";
import { SectionHeading } from "@/components/PageShell";

const STATUS_BADGE = { live: null, under_offer: "Under offer", sold: "Sold" };

export const ListingCard = ({ listing, index = 0 }) => {
  const cover = listing.photos?.[0];
  const price = "Price on application";
  const specs = Object.entries(listing.specs || {}).slice(0, 3);
  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.6, delay: (index % 3) * 0.1 }}
      data-testid={`listing-card-${listing.id}`} className="group bg-surface border border-white/5 rounded-sm overflow-hidden flex flex-col transition-colors duration-500 hover:border-gold/40"
    >
      <Link to={`/listings/${listing.id}`} className="relative aspect-[16/10] overflow-hidden block">
        {cover ? <img src={photoUrl(cover)} alt={listing.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
               : <div className="w-full h-full bg-white/5" />}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
        <div className="absolute top-4 left-4 flex gap-2">
          <span className="text-[10px] uppercase tracking-[0.2em] bg-black/60 backdrop-blur px-2.5 py-1 text-white/80">{listing.category}</span>
          {listing.discreet && <span className="text-[10px] uppercase tracking-[0.2em] bg-gold/90 px-2.5 py-1 text-black">Off-market</span>}
          {STATUS_BADGE[listing.status] && <span className="text-[10px] uppercase tracking-[0.2em] bg-white/90 px-2.5 py-1 text-black">{STATUS_BADGE[listing.status]}</span>}
          {listing.is_example && <span className="text-[10px] uppercase tracking-[0.2em] bg-black/60 backdrop-blur px-2.5 py-1 text-white/50">Example</span>}
        </div>
      </Link>
      <div className="p-6 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-serif text-2xl text-white leading-tight">{listing.title}</h3>
            <p className="text-xs text-white/50 mt-1">{[listing.year, listing.location].filter(Boolean).join(" · ")}</p>
          </div>
          <p className="text-sm text-gold whitespace-nowrap" data-testid={`listing-price-${listing.id}`}>{price}</p>
        </div>
        {specs.length > 0 && (
          <dl className="mt-5 grid grid-cols-3 gap-3 text-xs">
            {specs.map(([k, v]) => (<div key={k}><dt className="text-white/40 uppercase tracking-wider text-[9px]">{k}</dt><dd className="text-white/80 mt-0.5">{v}</dd></div>))}
          </dl>
        )}
        <div className="mt-6 pt-5 border-t border-white/5 flex items-center justify-between">
          <Link to={`/listings/${listing.id}`} className="text-[11px] uppercase tracking-[0.2em] text-white/70 hover:text-gold transition-colors" data-testid={`listing-view-${listing.id}`}>View details</Link>
          <Link to={`/enquire/${listing.category}?listing=${listing.id}&title=${encodeURIComponent(listing.title)}&intent=${encodeURIComponent(listing.category === "yacht" ? "Buy a yacht" : "Buy an aircraft")}`}
                className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-gold hover:text-gold-light transition-colors" data-testid={`listing-enquire-${listing.id}`}>
            Enquire <ArrowUpRight size={12} />
          </Link>
        </div>
      </div>
    </motion.article>
  );
};

export const FeaturedListings = () => {
  const [items, setItems] = useState([]);
  useEffect(() => { api("/listings?featured=true").then(setItems).catch(() => {}); }, []);
  if (!items.length) return null;
  return (
    <section id="featured" data-testid="featured-listings-section" className="py-24 lg:py-32 bg-obsidian border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <SectionHeading overline="Current mandates" title="Selected aircraft and yachts" />
          <div className="mb-14 flex gap-6 text-[11px] uppercase tracking-[0.2em]">
            <Link to="/aircraft-for-sale" className="text-white/60 hover:text-gold transition-colors" data-testid="featured-all-aircraft-link">All aircraft</Link>
            <Link to="/yachts" className="text-white/60 hover:text-gold transition-colors" data-testid="featured-all-yachts-link">All yachts</Link>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.slice(0, 3).map((l, i) => <ListingCard key={l.id} listing={l} index={i} />)}
        </div>
      </div>
    </section>
  );
};
